import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { useAnalytics } from '@/hooks/useAnalytics';

type Defaults = {
  email: string;
};

type State = {
  isOpen: boolean;
  open: (args?: Partial<Defaults>) => void;
  /**
   * Opened by the first-run guide, which keeps its own card on screen alongside
   * the modal. The focus trap would fight that card for focus, so it yields.
   */
  openGuided: () => void;
  isGuided: boolean;
  isGuideHolding: boolean;
  setGuideHold: (isGuideHolding: boolean) => void;
  close: () => void;
  defaults: Partial<Defaults>;
};

const useStore = createWithEqualityFn<State>()(
  (set, get) => ({
    isOpen: false,
    isGuided: false,
    isGuideHolding: false,
    setGuideHold: (isGuideHolding) =>
      set(({ isOpen }) => ({
        isGuideHolding,
        isGuided: isOpen && isGuideHolding,
      })),
    defaults: {},
    open: (defaults: Partial<Defaults> = {}) => {
      set({
        defaults,
        isOpen: true,
        isGuided: get().isGuideHolding,
      });
    },
    openGuided: () => set({ defaults: {}, isOpen: true, isGuided: true }),
    close: () => set({ isOpen: false, isGuided: false }),
  }),
  shallow
);

type Selector = (state: State) => Partial<State>;

export const useShareModalStore = <
  S extends Selector,
  Ret = S extends undefined ? ReturnType<S> : State,
>(
  selector?: S
): Ret => {
  const { trackEvent } = useAnalytics();
  const state = useStore(selector || ((s) => s));
  if (!state.open) {
    return state as Ret;
  }
  return {
    ...state,
    open: (defaults: Partial<Defaults> = {}) => {
      trackEvent('experience:click open-collaborator-share');
      state.open?.(defaults);
    },
  } as Ret;
};

export const useOpenShareModal = () => {
  const { open } = useShareModalStore((state) => ({ open: state.open }));
  return { openShareModal: open };
};
